import { AuthProvider, useAuth } from "./lib/AuthContext";
import Login from "./pages/Login";
import { AppProvider, useApp } from "./lib/AppContext";
import { ToastProvider } from "./components/shared/Toast";

// Your existing app content - import your router/pages here
import MainApp from "./MainApp"; // or whatever your main component is called

function CompanyScopedApp() {
  const { switchingOrg, orgSwitchError, orgId, setSelectedOrgId } = useApp();
  const { signOut } = useAuth();
  if (switchingOrg || orgSwitchError) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 text-center" role="status">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            {switchingOrg ? "Switching company..." : "Company access needs verification"}
          </h2>
          {orgSwitchError && <>
            <p className="text-gray-600 mb-4">{orgSwitchError}</p>
            <button className="text-blue-600 font-medium mr-4" onClick={() => setSelectedOrgId(orgId)}>
              Retry previous company
            </button>
            <button className="text-blue-600 font-medium" onClick={() => { signOut().catch(() => {}); }}>
              Sign out
            </button>
          </>}
        </div>
      </div>
    );
  }
  return <ToastProvider><MainApp /></ToastProvider>;
}

function AuthenticatedApp() {
  const { loading, isAuthenticated, error, logoutRecovery, signOut } = useAuth();

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error if employee record not found
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-6 h-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            {logoutRecovery ? "Sign-out needs attention" : "Access Error"}
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          {(!logoutRecovery || logoutRecovery.localCleared) && <button
            onClick={() => window.location.reload()}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            {logoutRecovery ? "Continue to sign in" : "Try Again"}
          </button>}
          {!logoutRecovery?.localCleared && <button
            onClick={() => { signOut().catch(() => {}); }}
            className="block mx-auto mt-4 text-blue-600 hover:text-blue-700 font-medium"
          >
            {logoutRecovery ? "Retry sign-out" : "Use a different account"}
          </button>}
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login />;
  }

  // User is authenticated and has employee record - show main app
  return (
    <AppProvider>
      <CompanyScopedApp />
    </AppProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AuthenticatedApp />
    </AuthProvider>
  );
}
